import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  Stack,
  Divider,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  alpha,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  IconButton,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  Save,
  ArrowBack,
  Layers,
  Terrain,
  Assignment,
  EditNote,
  WaterDrop,
  Grain,
  Delete,
  Add,
  CameraAlt,
} from "@mui/icons-material";
import api, { base_URL } from "../../api";
import {
  FIZYOGRAFYA_OPTIONS,
  ANA_MATERYAL_OPTIONS,
  BTG_OPTIONS,
  EGIM_YUZDE_OPTIONS,
  TASLILIK_OPTIONS,
  CAKILLILIK_OPTIONS,
  KAYALILIK_OPTIONS,
  URUN_OPTIONS,
  SU_EROZYON_DERECESI_OPTIONS,
  TABII_DRENAJ_OPTIONS,
  ARAZI_KULLANIM_KATEGORI_OPTIONS,
  ARAZI_KULLANIM_DETAY_OPTIONS,
  EGIM_YONU_OPTIONS,
  SEL_BASMASI_OPTIONS,
  TASKIN_OLASILIGI_OPTIONS,
  EROZYON_SEKLI_OPTIONS,
  RUZGAR_ASINDIRMA_OPTIONS,
  RUZGAR_YIGILMA_OPTIONS,
  INSAN_ETKISI_OPTIONS,
  YUZEY_CATLAK_OPTIONS,
  TUZLULUK_OPTIONS,
  ALKALILIK_OPTIONS,
  YUZEY_TOPOGRAFYASI_OPTIONS,
} from "../../constants/fieldConstants";

import {
  SINIR_BELIRGINLIK_OPTIONS,
  SINIR_SEKIL_OPTIONS,
  TEKSTUR_OPTIONS,
  STRUKTUR_DAYANIKLILIK,
  STRUKTUR_BUYUKLUK,
  STRUKTUR_TIP,
  KIVAM_KURU,
  KIVAM_NEMLI,
  KIVAM_YAS_YAPISKANLIK,
  KIVAM_YAS_PLASTIKLIK,
  KIREC_OPTIONS,
  KOK_MIKTAR_OPTIONS,
  KOK_KALINLIK_OPTIONS,
  MUNSELL_HUE_OPTIONS,
  MUNSELL_VALUE_CHROMA_OPTIONS,
} from "../../constants/horizonConstants";

import { PROFIL_GRUP_OPTIONS } from "../../constants/identityConstants";
import { calculateAkkClass } from "../../utils/akkUtils";
import { calculateStorieIndex } from "../../utils/storieUtils";
import DataTableFrame from "../../components/common/DataTableFrame";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// TEKLİ SEÇİM İÇİN AUTOCOMPLETE

const RenderSelect = ({ label, value, options = [], onChange }) => {
  // null, undefined veya sayısal değerlerin Autocomplete'i çökertmesini önler
  const safeValue = value === null || value === undefined ? "" : String(value);

  const safeOptions = Array.isArray(options)
    ? options.filter((option) => option !== null && option !== undefined)
    : [];

  return (
    <Autocomplete
      freeSolo
      options={safeOptions}
      value={safeValue}
      inputValue={safeValue}
      getOptionLabel={(option) =>
        option === null || option === undefined ? "" : String(option)
      }
      isOptionEqualToValue={(option, selectedValue) =>
        String(option ?? "") === String(selectedValue ?? "")
      }
      onInputChange={(event, newInputValue, reason) => {
        // Seçim yapıldığında "reset" olayı da çalışır.
        // Onu onChange kısmı yönetecek.
        if (reason === "input" || reason === "clear") {
          onChange(newInputValue ?? "");
        }
      }}
      onChange={(event, newValue) => {
        onChange(
          newValue === null || newValue === undefined ? "" : String(newValue),
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} size="small" />
      )}
    />
  );
};
// ÇOKLU SEÇİM İÇİN AUTOCOMPLETE (İnsan Etkisi, Erozyon Şekli vb.)
const RenderMultiSelect = ({ label, value, options = [], onChange }) => {
  const safeValue = Array.isArray(value)
    ? value.filter((item) => item !== null && item !== undefined)
    : [];

  const safeOptions = Array.isArray(options)
    ? options.filter((option) => option !== null && option !== undefined)
    : [];

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={safeOptions}
      value={safeValue}
      getOptionLabel={(option) =>
        option === null || option === undefined ? "" : String(option)
      }
      isOptionEqualToValue={(option, selectedValue) =>
        String(option ?? "") === String(selectedValue ?? "")
      }
      onChange={(event, newValue) => {
        onChange(Array.isArray(newValue) ? newValue : []);
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            sx={{ mr: 1 }}
            checked={selected}
          />

          {String(option ?? "")}
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} size="small" variant="outlined" />
      )}
    />
  );
};

// --- YARDIMCI BİLEŞEN: BÖLÜM BAŞLIĞI ---
const EditSubHeader = ({ title, icon, color }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ mb: 2, mt: 4 }}
  >
    <Box
      sx={{
        display: "flex",
        p: 0.8,
        borderRadius: "8px",
        bgcolor: alpha(color, 0.1),
        color: color,
      }}
    >
      {React.cloneElement(icon, { fontSize: "small" })}
    </Box>
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: "text.primary",
      }}
    >
      {title}
    </Typography>
    <Divider sx={{ flexGrow: 1, borderColor: alpha(color, 0.2) }} />
  </Stack>
);

export default function ProfileEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Ana state: Tüm veriyi burada topluyoruz
  const [formData, setFormData] = useState({
    profil_no: "",
    ilce_mahalle: "",
    cuval_no: "",
    details: {},
  });

  const thumbnailStyle = {
    width: "100%",
    height: { xs: 120, md: 220 },
    objectFit: "cover",
    borderRadius: 12,
    border: "2px solid #eee",
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": { transform: "scale(1.02)" },
  };

  // --- RESİM YÜKLEME VE SİLME FONKSİYONLARI ---
  const handleImageUpload = async (e, type) => {
    const files = Array.from(e.target.files);

    if (type === "horizon") {
      if (formData.details.resim_horizon)
        return alert("Zaten yüklü. Önce siliniz.");

      const file = files[0];

      if (file) {
        const reader = new FileReader();
        reader.onloadend = () =>
          handleDetailChange("resim_horizon", reader.result);

        reader.readAsDataURL(file);
      }
    }

    if (type === "cevre") {
      const readFile = (file, index) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = () =>
            resolve({
              index,
              image: reader.result,
            });

          reader.onerror = reject;

          reader.readAsDataURL(file);
        });

      // Dosyaları seçilme sırasıyla oku
      const images = await Promise.all(
        files.map((file, index) => readFile(file, index)),
      );

      // Okuma sırası değişse bile eski sıraya getir
      images.sort((a, b) => a.index - b.index);

      setFormData((prev) => ({
        ...prev,
        details: {
          ...prev.details,
          resimler_cevre: [
            ...(prev.details.resimler_cevre || []),
            ...images.map((i) => i.image),
          ],
        },
      }));
    }
    // Aynı dosyayı tekrar seçebilmek için
    e.target.value = "";
  };

  const removeImage = (type, index = null) => {
    if (type === "horizon") {
      handleDetailChange("resim_horizon", null);
    } else if (type === "cevre") {
      const newCevre = (formData.details.resimler_cevre || []).filter(
        (_, i) => i !== index,
      );
      handleDetailChange("resimler_cevre", newCevre);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);

        const res = await api.get(`/admin/profile/${id}`);
        const profile = res.data.data;

        let parsedDetails = {};

        if (profile.full_data) {
          parsedDetails =
            typeof profile.full_data === "string"
              ? JSON.parse(profile.full_data)
              : profile.full_data;
        }

        if (
          !parsedDetails ||
          typeof parsedDetails !== "object" ||
          Array.isArray(parsedDetails)
        ) {
          parsedDetails = {};
        }

        setFormData({
          profil_no: profile.profil_no ?? "",
          ilce_mahalle: profile.ilce_mahalle ?? "",
          cuval_no: profile.cuval_no ?? "",
          details: parsedDetails,
        });
      } catch (err) {
        console.error("Profil yükleme hatası:", err);
        alert("Profil bilgileri yüklenemedi.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  // Nested JSON alanlarını (details) güncelleme
  const handleDetailChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      details: { ...prev.details, [field]: value },
    }));
  };

  // Horizons (Katmanlar) dizisini güncelleme
  const handleHorizonChange = (index, field, value) => {
    const newHorizons = [...(formData.details.horizons || [])];
    newHorizons[index] = { ...newHorizons[index], [field]: value };
    handleDetailChange("horizons", newHorizons);
  };

  const handleSave = async () => {
    try {
      // 1. Girdiğimiz güncel detaylara göre formülleri yeniden hesaplatıyoruz
      const akkResult = calculateAkkClass(formData.details);
      const storieResult = calculateStorieIndex(formData.details);

      // 2. İlçe ve mahalle birleşimi için yeni lokasyon stringini oluşturuyoruz
      // Böylece il/ilçe değiştiğinde dış sütun da güncellenecek
      const fullLocation = [
        formData.details.il,
        formData.details.ilce,
        formData.details.mahalle,
      ]
        .filter((part) => part && part.trim() !== "")
        .join(" / ");

      // 3. Çıkan sonuçları ve üst seviye değerleri (profil no vb) JSON içine yedekle
      const finalDetails = {
        ...formData.details,
        profil_no: formData.profil_no, // Profil no'yu iç JSON ile senkronize et
        cuval_no: formData.cuval_no, // Çuval no'yu iç JSON ile senkronize et
        yeri: fullLocation, // Lokasyonu iç JSON'a yaz
        akk_class_result: akkResult.finalClass + akkResult.subClassString,
        storie_index_result: storieResult.totalIndex || storieResult.finalIndex,
        storie_class_result: storieResult.storieClass,
      };

      // 4. Güncellenmiş sonuçlarla beraber Payload'u oluşturuyoruz
      const payload = {
        profil_no: formData.profil_no,
        ilce_mahalle: fullLocation, // Tablodaki Listeleme Sütununu Güncelliyoruz
        cuval_no: formData.cuval_no,
        full_data: finalDetails,
      };

      await api.put(`/admin/update-profile/${id}`, payload);
      alert("✅ Profil başarıyla güncellendi!");
      navigate("/admin/profiles");
    } catch (err) {
      alert("❌ Güncelleme sırasında hata oluştu!");
    }
  };

  if (loading)
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>Veriler hazırlanıyor...</Box>
    );

  return (
    <Box sx={{ p: 3, bgcolor: "#f4f6f8", minHeight: "100vh" }}>
      {/* ÜST AKSİYON BAR */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          variant="outlined"
          sx={{ borderRadius: "10px" }}
        >
          İptal / Geri
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 900, color: "#2c3e50" }}>
          PROFİL DÜZENLEME PANELİ
        </Typography>
        <Button
          variant="contained"
          color="success"
          startIcon={<Save />}
          onClick={handleSave}
          sx={{ borderRadius: "10px", px: 4, py: 1.2, fontWeight: "bold" }}
        >
          Verileri Güncelle
        </Button>
      </Stack>
      {/* --- 1. GENEL & KONUM BİLGİLERİ --- */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: "16px" }}>
        <EditSubHeader
          title="1. Genel Proje & Konum Bilgileri"
          icon={<Assignment />}
          color="#1976d2"
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
          }}
        >
          <TextField
            label="Profil No"
            size="small"
            value={formData.profil_no}
            onChange={(e) =>
              setFormData({ ...formData, profil_no: e.target.value })
            }
          />
          <TextField
            label="Çuval No"
            size="small"
            value={formData.cuval_no}
            onChange={(e) =>
              setFormData({ ...formData, cuval_no: e.target.value })
            }
          />
          <TextField
            label="Tanımlayan"
            size="small"
            value={formData.details.tanimlayan || ""}
            onChange={(e) => handleDetailChange("tanimlayan", e.target.value)}
          />
          <TextField
            label="Tarih"
            type="date"
            size="small"
            value={formData.details.tarih || ""}
            onChange={(e) => handleDetailChange("tarih", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Şehir / İl"
            size="small"
            value={formData.details.il || ""}
            onChange={(e) => handleDetailChange("il", e.target.value)}
          />
          <TextField
            label="İlçe"
            size="small"
            value={formData.details.ilce || ""}
            onChange={(e) => handleDetailChange("ilce", e.target.value)}
          />
          <TextField
            label="Mahalle / Mevkii"
            size="small"
            value={formData.details.mahalle || ""}
            onChange={(e) => handleDetailChange("mahalle", e.target.value)}
          />

          <RenderSelect
            label="Profil Grubu"
            value={formData.details.profilgrup}
            options={PROFIL_GRUP_OPTIONS}
            onChange={(val) => handleDetailChange("profilgrup", val)}
          />

          <TextField
            label="Koordinat X (Enlem)"
            size="small"
            value={formData.details.koordinat_x || ""}
            onChange={(e) => handleDetailChange("koordinat_x", e.target.value)}
          />
          <TextField
            label="Koordinat Y (Boylam)"
            size="small"
            value={formData.details.koordinat_y || ""}
            onChange={(e) => handleDetailChange("koordinat_y", e.target.value)}
          />
          <TextField
            label="Yükseklik (Rakım - m)"
            size="small"
            type="number"
            value={formData.details.yukseklik || ""}
            onChange={(e) => handleDetailChange("yukseklik", e.target.value)}
          />
        </Box>
      </Paper>
      {/* --- 2. ARAZİ KARAKTERİSTİĞİ --- */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: "16px" }}>
        <EditSubHeader
          title="2. Fizyografya, Jeoloji & Arazi Kullanımı"
          icon={<Terrain />}
          color="#2e7d32"
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            gap: 2,
          }}
        >
          <RenderSelect
            label="BTG (Büyük Toprak Grubu)"
            value={formData.details.btg}
            options={BTG_OPTIONS}
            onChange={(val) => handleDetailChange("btg", val)}
          />
          <RenderSelect
            label="Ana Materyal"
            value={formData.details.ana_materyal}
            options={ANA_MATERYAL_OPTIONS}
            onChange={(val) => handleDetailChange("ana_materyal", val)}
          />
          <RenderSelect
            label="Fizyografya"
            value={formData.details.fizyografya}
            options={FIZYOGRAFYA_OPTIONS}
            onChange={(val) => handleDetailChange("fizyografya", val)}
          />

          <TextField
            label="Kaya Tipi"
            size="small"
            value={formData.details.kaya_tipi || ""}
            onChange={(e) => handleDetailChange("kaya_tipi", e.target.value)}
          />
          <TextField
            label="Mikrorölyef"
            size="small"
            value={formData.details.mikrorolyef || ""}
            onChange={(e) => handleDetailChange("mikrorolyef", e.target.value)}
          />
          <TextField
            label="Yüzey Topoğrafyası (Rölyef)"
            size="small"
            value={formData.details.yuzey_topografyasi || ""}
            onChange={(e) =>
              handleDetailChange("yuzey_topografyasi", e.target.value)
            }
          />

          <RenderSelect
            label="Arazi Kullanım Sınıfı"
            value={formData.details.arazi_kullanim_sinif}
            options={ARAZI_KULLANIM_KATEGORI_OPTIONS}
            onChange={(val) => handleDetailChange("arazi_kullanim_sinif", val)}
          />
          <RenderSelect
            label="Arazi Kullanım Detay"
            value={formData.details.arazi_kullanim_detay}
            options={ARAZI_KULLANIM_DETAY_OPTIONS}
            onChange={(val) => handleDetailChange("arazi_kullanim_detay", val)}
          />
          <RenderSelect
            label="Mevcut Ürün"
            value={formData.details.urun}
            options={URUN_OPTIONS}
            onChange={(val) => handleDetailChange("urun", val)}
          />

          <TextField
            label="Toprak Serisi"
            size="small"
            value={formData.details.toprak_serisi || ""}
            onChange={(e) =>
              handleDetailChange("toprak_serisi", e.target.value)
            }
          />
          <TextField
            label="Seri Kodu"
            size="small"
            value={formData.details.seri_kodu || ""}
            onChange={(e) => handleDetailChange("seri_kodu", e.target.value)}
          />
        </Box>
      </Paper>
      {/* --- 3. DRENAJ, EĞİM & EROZYON FAKTÖRLERİ --- */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: "16px" }}>
        <EditSubHeader
          title="3. Hidroloji, Eğim & Erozyon Faktörleri"
          icon={<WaterDrop />}
          color="#0288d1"
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
          }}
        >
          <TextField
            label="Tesirli Derinlik (cm)"
            size="small"
            type="number"
            value={formData.details.tesirli_derinlik || ""}
            onChange={(e) =>
              handleDetailChange("tesirli_derinlik", e.target.value)
            }
          />
          <TextField
            label="Kök Gel. Derinlik (cm)"
            size="small"
            type="number"
            value={formData.details.kok_gel_derinlik || ""}
            onChange={(e) =>
              handleDetailChange("kok_gel_derinlik", e.target.value)
            }
          />
          <TextField
            label="Kök Engel Faktör"
            size="small"
            value={formData.details.kok_gel_eng_faktor || ""}
            onChange={(e) =>
              handleDetailChange("kok_gel_eng_faktor", e.target.value)
            }
          />

          <RenderSelect
            label="Eğim (%)"
            value={formData.details.egim_yuzde}
            options={EGIM_YUZDE_OPTIONS}
            onChange={(val) => handleDetailChange("egim_yuzde", val)}
          />
          <RenderSelect
            label="Eğim Yönü (Bakı)"
            value={formData.details.egim_yonu}
            options={EGIM_YONU_OPTIONS}
            onChange={(val) => handleDetailChange("egim_yonu", val)}
          />
          <RenderSelect
            label="Tabii Drenaj"
            value={formData.details.tabii_drenaj}
            options={TABII_DRENAJ_OPTIONS}
            onChange={(val) => handleDetailChange("tabii_drenaj", val)}
          />
          <RenderSelect
            label="Su Erozyon Derecesi"
            value={formData.details.su_erozyon_derecesi}
            options={SU_EROZYON_DERECESI_OPTIONS}
            onChange={(val) => handleDetailChange("su_erozyon_derecesi", val)}
          />

          <RenderMultiSelect
            label="Erozyon Şekli"
            value={formData.details.erozyon_sekli}
            options={EROZYON_SEKLI_OPTIONS}
            onChange={(val) => handleDetailChange("erozyon_sekli", val)}
          />

          <RenderSelect
            label="Rüzgar Aşındırma"
            value={formData.details.ruzgar_asinma}
            options={RUZGAR_ASINDIRMA_OPTIONS}
            onChange={(val) => handleDetailChange("ruzgar_asinma", val)}
          />
          <RenderSelect
            label="Rüzgar Yığılma"
            value={formData.details.ruzgar_yigilma}
            options={RUZGAR_YIGILMA_OPTIONS}
            onChange={(val) => handleDetailChange("ruzgar_yigilma", val)}
          />
          <RenderSelect
            label="Sel Basması"
            value={formData.details.sel_basmasi}
            options={SEL_BASMASI_OPTIONS}
            onChange={(val) => handleDetailChange("sel_basmasi", val)}
          />
          <RenderSelect
            label="Taşkın Olasılığı"
            value={formData.details.taskin_olasiligi}
            options={TASKIN_OLASILIGI_OPTIONS}
            onChange={(val) => handleDetailChange("taskin_olasiligi", val)}
          />

          <TextField
            label="Taban Suyu Min (cm)"
            size="small"
            type="number"
            value={formData.details.taban_suyu_min || ""}
            onChange={(e) =>
              handleDetailChange("taban_suyu_min", e.target.value)
            }
          />
          <TextField
            label="Taban Suyu Max (cm)"
            size="small"
            type="number"
            value={formData.details.taban_suyu_max || ""}
            onChange={(e) =>
              handleDetailChange("taban_suyu_max", e.target.value)
            }
          />
          <TextField
            label="Taban Suyu Belirtisi"
            size="small"
            value={formData.details.taban_suyu_belirti || ""}
            onChange={(e) =>
              handleDetailChange("taban_suyu_belirti", e.target.value)
            }
          />
        </Box>
      </Paper>
      {/* --- 4. YÜZEY ÖZELLİKLERİ & İKLİM --- */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: "16px" }}>
        <EditSubHeader
          title="4. Yüzey Durumu & İklim Verileri"
          icon={<Grain />}
          color="#ef6c00"
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
          }}
        >
          <RenderSelect
            label="Taşlılık"
            value={formData.details.taslilik}
            options={TASLILIK_OPTIONS}
            onChange={(val) => handleDetailChange("taslilik", val)}
          />
          <RenderSelect
            label="Çakıllık"
            value={formData.details.cakillilik}
            options={CAKILLILIK_OPTIONS}
            onChange={(val) => handleDetailChange("cakillilik", val)}
          />
          <RenderSelect
            label="Kayalılık"
            value={formData.details.kayalilik}
            options={KAYALILIK_OPTIONS}
            onChange={(val) => handleDetailChange("kayalilik", val)}
          />
          <RenderSelect
            label="Yüzey Çatlağı"
            value={formData.details.yuzey_catlak}
            options={YUZEY_CATLAK_OPTIONS}
            onChange={(val) => handleDetailChange("yuzey_catlak", val)}
          />

          <RenderMultiSelect
            label="İnsan Etkisi"
            value={formData.details.insan_etkisi}
            options={INSAN_ETKISI_OPTIONS}
            onChange={(val) => handleDetailChange("insan_etkisi", val)}
          />

          <RenderSelect
            label="Tuzluluk"
            value={formData.details.tuzluluk}
            options={TUZLULUK_OPTIONS}
            onChange={(val) => handleDetailChange("tuzluluk", val)}
          />
          <RenderSelect
            label="Alkalilik"
            value={formData.details.alkalilik}
            options={ALKALILIK_OPTIONS}
            onChange={(val) => handleDetailChange("alkalilik", val)}
          />

          <TextField
            label="İklim Tipi"
            size="small"
            value={formData.details.iklim || ""}
            onChange={(e) => handleDetailChange("iklim", e.target.value)}
          />
          <TextField
            label="Ort. Sıcaklık (°C)"
            size="small"
            type="number"
            value={formData.details.ort_sicaklik || ""}
            onChange={(e) => handleDetailChange("ort_sicaklik", e.target.value)}
          />
          <TextField
            label="Ort. Yağış (mm)"
            size="small"
            type="number"
            value={formData.details.ort_yagis || ""}
            onChange={(e) => handleDetailChange("ort_yagis", e.target.value)}
          />

          {/* Bu iki alan 3. adımda zaten var, ancak orijinal gönderdiğin blokta da yer alıyorlardı. 
              Mükerrer olmasını istemiyorsan bu iki TextField'ı silebilirsin. Ben sadık kalmak adına bıraktım. */}
          <TextField
            label="Tesirli Derinlik (cm)"
            size="small"
            type="number"
            value={formData.details.tesirli_derinlik || ""}
            onChange={(e) =>
              handleDetailChange("tesirli_derinlik", e.target.value)
            }
          />
          <TextField
            label="Kök Engel Faktör"
            size="small"
            value={formData.details.kok_gel_eng_faktor || ""}
            onChange={(e) =>
              handleDetailChange("kok_gel_eng_faktor", e.target.value)
            }
          />
        </Box>
      </Paper>
      {/* --- 5. KATMANLAR (HORIZONS) - EN DETAYLI BÖLÜM --- */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: "16px", overflow: "hidden" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <EditSubHeader
            title="5. Horizon (Katman) Teknik Analizleri"
            icon={<Layers />}
            color="#7b1fa2"
          />
          {/* YENİ: Katman Ekle Butonu */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<Add />}
            onClick={() => {
              const newHorizons = [...(formData.details.horizons || [])];
              const lastHorizon = newHorizons[newHorizons.length - 1];
              const startDepth = lastHorizon
                ? parseFloat(lastHorizon.derinlik_max) || 0
                : 0;

              newHorizons.push({
                no: newHorizons.length + 1,
                sembol: "",
                derinlik_min: startDepth,
                derinlik_max: "",
                derinlik_arti: false,
                sinir_belirginlik: "",
                sinir_sekil: "",
                renk_sinif_kuru: "",
                renk_kod_kuru: "",
                renk_sinif_yas: "",
                renk_kod_yas: "",
                tekstur: "",
                struktur_dayaniklilik: "",
                struktur_buyukluk: "",
                struktur_tip: [],
                kivam_kuru: "",
                kivam_nemli: "",
                kivam_yas_yapiskanlik: "",
                kivam_yas_plastiklik: "",
                kirec: "",
                taslilik: "",
                kok_sacak_miktar: "",
                kok_sacak_kalinlik: "",
                kok_kazik_miktar: "",
                kok_kazik_kalinlik: "",
                ozel_gorunum: "",
              });
              handleDetailChange("horizons", newHorizons);
            }}
          >
            Yeni Katman Ekle
          </Button>
        </Stack>

        <DataTableFrame
          plain
          size="small"
          minWidth={1200}
          maxHeight={null}
          stickyHeader={false}
        >
            <TableHead sx={{ bgcolor: "#f8f9fa" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, width: 80 }}>
                  Sembol
                </TableCell>
                <TableCell sx={{ fontWeight: 800, width: 140 }}>
                  Derinlik (Min-Max)
                </TableCell>
                <TableCell sx={{ fontWeight: 800, width: 150 }}>
                  Tekstür & Kireç
                </TableCell>
                <TableCell sx={{ fontWeight: 800, width: 200 }}>
                  Strüktür (Tip/Büy/Day)
                </TableCell>
                <TableCell sx={{ fontWeight: 800, width: 150 }}>
                  Kıvam (K/N/Y)
                </TableCell>
                <TableCell sx={{ fontWeight: 800, width: 180 }}>
                  Munsell Renk (Kuru/Yaş)
                </TableCell>
                <TableCell sx={{ fontWeight: 800, width: 180 }}>
                  Kökler (Saçak/Kazık)
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 800, width: 50 }}>
                  Sil
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.details.horizons?.map((h, idx) => (
                <TableRow key={idx} hover>
                  {/* Sembol */}
                  <TableCell valign="top">
                    <TextField
                      fullWidth
                      variant="standard"
                      value={h.sembol || ""}
                      onChange={(e) =>
                        handleHorizonChange(idx, "sembol", e.target.value)
                      }
                    />
                  </TableCell>

                  {/* Derinlikler */}
                  <TableCell valign="top">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TextField
                        size="small"
                        type="number"
                        sx={{ width: 55 }}
                        value={h.derinlik_min === 0 ? 0 : h.derinlik_min || ""}
                        onChange={(e) =>
                          handleHorizonChange(
                            idx,
                            "derinlik_min",
                            e.target.value,
                          )
                        }
                      />
                      <Typography>-</Typography>
                      <TextField
                        size="small"
                        type="number"
                        sx={{ width: 55 }}
                        value={h.derinlik_max || ""}
                        disabled={h.derinlik_arti}
                        onChange={(e) =>
                          handleHorizonChange(
                            idx,
                            "derinlik_max",
                            e.target.value,
                          )
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={h.derinlik_arti || false}
                            onChange={(e) => {
                              handleHorizonChange(
                                idx,
                                "derinlik_arti",
                                e.target.checked,
                              );
                              if (e.target.checked)
                                handleHorizonChange(idx, "derinlik_max", 200);
                            }}
                          />
                        }
                        label="+"
                        sx={{ m: 0 }}
                      />
                    </Stack>
                  </TableCell>

                  {/* Tekstür & Kireç & Sınır */}
                  <TableCell valign="top">
                    <Stack spacing={1}>
                      <RenderSelect
                        label="Tekstür"
                        value={h.tekstur}
                        options={TEKSTUR_OPTIONS}
                        onChange={(val) =>
                          handleHorizonChange(idx, "tekstur", val)
                        }
                      />
                      <RenderSelect
                        label="Kireç"
                        value={h.kirec}
                        options={KIREC_OPTIONS}
                        onChange={(val) =>
                          handleHorizonChange(idx, "kirec", val)
                        }
                      />
                      <Stack direction="row" spacing={1}>
                        <Box sx={{ width: "50%" }}>
                          <RenderSelect
                            label="Sınır Bel."
                            value={h.sinir_belirginlik}
                            options={SINIR_BELIRGINLIK_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "sinir_belirginlik", val)
                            }
                          />
                        </Box>
                        <Box sx={{ width: "50%" }}>
                          <RenderSelect
                            label="Sınır Şekli"
                            value={h.sinir_sekil}
                            options={SINIR_SEKIL_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "sinir_sekil", val)
                            }
                          />
                        </Box>
                      </Stack>
                    </Stack>
                  </TableCell>

                  {/* Strüktür */}
                  <TableCell valign="top">
                    <Stack spacing={1}>
                      <RenderMultiSelect
                        label="Tip"
                        value={h.struktur_tip}
                        options={STRUKTUR_TIP}
                        onChange={(val) =>
                          handleHorizonChange(idx, "struktur_tip", val)
                        }
                      />
                      <RenderSelect
                        label="Büyüklük"
                        value={h.struktur_buyukluk}
                        options={STRUKTUR_BUYUKLUK}
                        onChange={(val) =>
                          handleHorizonChange(idx, "struktur_buyukluk", val)
                        }
                      />
                      <RenderSelect
                        label="Dayanıklılık"
                        value={h.struktur_dayaniklilik}
                        options={STRUKTUR_DAYANIKLILIK}
                        onChange={(val) =>
                          handleHorizonChange(idx, "struktur_dayaniklilik", val)
                        }
                      />
                    </Stack>
                  </TableCell>

                  {/* Kıvam */}
                  <TableCell valign="top">
                    <Stack spacing={1}>
                      <RenderSelect
                        label="Kuru"
                        value={h.kivam_kuru}
                        options={KIVAM_KURU}
                        onChange={(val) =>
                          handleHorizonChange(idx, "kivam_kuru", val)
                        }
                      />
                      <RenderSelect
                        label="Nemli"
                        value={h.kivam_nemli}
                        options={KIVAM_NEMLI}
                        onChange={(val) =>
                          handleHorizonChange(idx, "kivam_nemli", val)
                        }
                      />
                      <RenderSelect
                        label="Y.Yapışkanlık"
                        value={h.kivam_yas_yapiskanlik}
                        options={KIVAM_YAS_YAPISKANLIK}
                        onChange={(val) =>
                          handleHorizonChange(idx, "kivam_yas_yapiskanlik", val)
                        }
                      />
                      <RenderSelect
                        label="Y.Plastiklik"
                        value={h.kivam_yas_plastiklik}
                        options={KIVAM_YAS_PLASTIKLIK}
                        onChange={(val) =>
                          handleHorizonChange(idx, "kivam_yas_plastiklik", val)
                        }
                      />
                    </Stack>
                  </TableCell>

                  {/* Munsell Renkler */}
                  <TableCell valign="top">
                    <Stack spacing={1}>
                      <Box
                        sx={{
                          border: "1px solid #ddd",
                          p: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "orange", fontWeight: "bold" }}
                        >
                          KURU
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <RenderSelect
                            label="Hue"
                            value={h.renk_sinif_kuru}
                            options={MUNSELL_HUE_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "renk_sinif_kuru", val)
                            }
                          />
                          <RenderSelect
                            label="Kod"
                            value={h.renk_kod_kuru}
                            options={MUNSELL_VALUE_CHROMA_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "renk_kod_kuru", val)
                            }
                          />
                        </Stack>
                      </Box>
                      <Box
                        sx={{
                          border: "1px solid #ddd",
                          p: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "blue", fontWeight: "bold" }}
                        >
                          YAŞ
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <RenderSelect
                            label="Hue"
                            value={h.renk_sinif_yas}
                            options={MUNSELL_HUE_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "renk_sinif_yas", val)
                            }
                          />
                          <RenderSelect
                            label="Kod"
                            value={h.renk_kod_yas}
                            options={MUNSELL_VALUE_CHROMA_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "renk_kod_yas", val)
                            }
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </TableCell>

                  {/* Kökler ve Özel Görünüm */}
                  <TableCell valign="top">
                    <Stack spacing={1}>
                      <Box
                        sx={{
                          border: "1px solid #ddd",
                          p: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold">
                          SAÇAK KÖK
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <RenderSelect
                            label="Miktar"
                            value={h.kok_sacak_miktar}
                            options={KOK_MIKTAR_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "kok_sacak_miktar", val)
                            }
                          />
                          <RenderSelect
                            label="Kalınlık"
                            value={h.kok_sacak_kalinlik}
                            options={KOK_KALINLIK_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(
                                idx,
                                "kok_sacak_kalinlik",
                                val,
                              )
                            }
                          />
                        </Stack>
                      </Box>
                      <Box
                        sx={{
                          border: "1px solid #ddd",
                          p: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold">
                          KAZIK KÖK
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <RenderSelect
                            label="Miktar"
                            value={h.kok_kazik_miktar}
                            options={KOK_MIKTAR_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "kok_kazik_miktar", val)
                            }
                          />
                          <RenderSelect
                            label="Kalınlık"
                            value={h.kok_kazik_kalinlik}
                            options={KOK_KALINLIK_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(
                                idx,
                                "kok_kazik_kalinlik",
                                val,
                              )
                            }
                          />
                        </Stack>
                      </Box>
                      <TextField
                        fullWidth
                        label="Özel Görünüm"
                        size="small"
                        multiline
                        rows={2}
                        value={h.ozel_gorunum || ""}
                        onChange={(e) =>
                          handleHorizonChange(
                            idx,
                            "ozel_gorunum",
                            e.target.value,
                          )
                        }
                      />
                    </Stack>
                  </TableCell>

                  {/* YENİ: Sil Butonu (En az 1 katman kalacak şekilde ayarlandı) */}
                  <TableCell valign="middle" align="center">
                    <IconButton
                      color="error"
                      disabled={formData.details.horizons?.length === 1}
                      onClick={() => {
                        if (
                          window.confirm(
                            `${idx + 1}. Katmanı silmek istediğinize emin misiniz?`,
                          )
                        ) {
                          const newHorizons = formData.details.horizons.filter(
                            (_, i) => i !== idx,
                          );
                          handleDetailChange("horizons", newHorizons);
                        }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
        </DataTableFrame>
      </Paper>
      {/* --- 6. GENEL NOTLAR --- */}
      <Paper sx={{ p: 3, borderRadius: "16px" }}>
        <EditSubHeader
          title="6. Arazi Açıklamaları & Notlar"
          icon={<EditNote />}
          color="#455a64"
        />
        <TextField
          fullWidth
          multiline
          rows={5}
          placeholder="Arazi hakkında eklemek istediğiniz diğer notlar..."
          value={formData.details.arazi_aciklama || ""}
          onChange={(e) => handleDetailChange("arazi_aciklama", e.target.value)}
        />
      </Paper>
      {/* --- 7. FOTOĞRAFLAR --- */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          bgcolor: "#fafafa",
          border: "1px dashed #cfd8dc",
        }}
      >
        <EditSubHeader
          title="7. Saha Fotoğrafları"
          icon={<CameraAlt />}
          color="#9c27b0"
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
            gap: 4,
          }}
        >
          {/* HORIZON FOTOĞRAFI */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="text.secondary"
              mb={2}
            >
              1. HORIZON (PROFİL)
            </Typography>
            {!formData.details.resim_horizon ? (
              <Button
                fullWidth
                variant="outlined"
                component="label"
                startIcon={<CameraAlt sx={{ fontSize: { md: 32 } }} />}
                sx={{
                  height: { xs: 120, md: 220 },
                  borderStyle: "dashed",
                  borderWidth: "2px",
                  borderRadius: 3,
                  fontWeight: "bold",
                }}
              >
                Fotoğraf Yükle
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, "horizon")}
                />
              </Button>
            ) : (
              <Box sx={{ position: "relative" }}>
                <Box
                  component="img"
                  // Eğer veritabanından /uploads/... geliyorsa başına sunucu URL'ni eklemeyi unutma (Örn: api url'niz)
                  // Base64 ise direkt çalışır.
                  src={
                    formData.details.resim_horizon.startsWith("/")
                      ? `${base_URL}${formData.details.resim_horizon}`
                      : formData.details.resim_horizon
                  }
                  sx={thumbnailStyle}
                  onClick={() =>
                    window.open(
                      formData.details.resim_horizon.startsWith("/")
                        ? `${base_URL}${formData.details.resim_horizon}`
                        : formData.details.resim_horizon,
                    )
                  }
                />
                <IconButton
                  onClick={() => removeImage("horizon")}
                  sx={{
                    position: "absolute",
                    top: -15,
                    right: -15,
                    bgcolor: "#fff",
                    boxShadow: 3,
                    border: "1px solid #ffcdd2",
                    "&:hover": { bgcolor: "#ffebee" },
                  }}
                >
                  <Delete color="error" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* ÇEVRE FOTOĞRAFLARI */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="text.secondary"
              mb={2}
            >
              2. ÇEVRE FOTOĞRAFLARI
            </Typography>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              {formData.details.resimler_cevre?.map((img, i) => (
                <Box key={i} sx={{ position: "relative" }}>
                  <Box
                    component="img"
                    src={img.startsWith("/") ? `${base_URL}${img}` : img}
                    sx={thumbnailStyle}
                    onClick={() =>
                      window.open(
                        img.startsWith("/") ? `${base_URL}${img}` : img,
                      )
                    }
                  />
                  <IconButton
                    onClick={() => removeImage("cevre", i)}
                    sx={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      bgcolor: "#fff",
                      boxShadow: 2,
                      "&:hover": { bgcolor: "#ffebee" },
                    }}
                  >
                    <Delete color="error" fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              {/* SINIRSIZ EKLEME BUTONU */}
              <Button
                fullWidth
                variant="outlined"
                component="label"
                startIcon={<CameraAlt sx={{ fontSize: { md: 32 } }} />}
                sx={{
                  height: { xs: 120, md: 220 },
                  borderStyle: "dashed",
                  borderWidth: "2px",
                  borderRadius: 3,
                  fontWeight: "bold",
                }}
              >
                Çevre Ekle
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImageUpload(e, "cevre")}
                />
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
      {/* ALT BUTON */}
      <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={<Save />}
          onClick={handleSave}
          sx={{
            borderRadius: "12px",
            px: 10,
            py: 2,
            fontSize: "1.1rem",
            fontWeight: "bold",
            boxShadow: "0 8px 30px rgba(46, 125, 50, 0.3)",
          }}
        >
          TÜM VERİLERİ KAYDET
        </Button>
      </Box>
    </Box>
  );
}
